/**
 * Evidence — and the one screen whose correctness is mostly about what it does NOT claim.
 *
 * **authoxi decides and forgets.** Every decision is signed and shipped to an evidence store;
 * `GET /agent/events` and its 1000-entry in-process ring were deleted on purpose, because an
 * auth service that also keeps the history has grown an audit product inside it and the two
 * will drift — the ring was volatile, per-process, lost on restart, invisible across replicas,
 * and it silently disagreed with the durable record more than once.
 *
 * So this renders what authoxi genuinely owns — settled approvals, the compliance pack, the
 * control-plane audit log — and points at the record for the rest.
 *
 * **The empty state must never say "No decisions yet."** It would be a lie the moment one agent
 * acted: the decisions happened, we are simply not the ones who keep them. An empty feed where
 * a full record exists answers the question *wrongly* rather than not answering it.
 */

import { State, TOOLS, fmtMoney, fmtWhen, useAsync } from "@authoxi/ui-shared";
import type { Api } from "../api";

export function Evidence({ api }: { api: Api }) {
  const settled = useAsync(() => api.pending("resolved"));
  const audit = useAsync(() => api.audit());
  const recent = useAsync(() => api.decisions({ hours: 168, limit: 1 }));

  const recordUrl = recent.data?.record_url ?? null;

  return (
    <>
      <div className="view-head">
        <span className="label">Evidence</span>
        <h1>The signed record.</h1>
        <p>
          authoxi decides and forgets. Every decision is signed and shipped to your evidence
          store — an auth service that also keeps the history has grown an audit product inside
          it, and the two will drift.
        </p>
      </div>

      <div className="card pad-lg" data-testid="record-elsewhere">
        <p className="label">Where the record lives</p>
        {recordUrl ? (
          <>
            <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
              Signed, sequenced and verifiable, at your evidence store. Every decision this gate
              made is there, numbered by this gate — which is what makes a suppressed record
              detectable rather than invisible.
            </p>
            <a className="btn btn-go" style={{ marginTop: "var(--s4)" }} href={recordUrl} rel="external">
              Open the record
            </a>
          </>
        ) : (
          // Null is honest and worth saying: it means nothing is receiving the signed events,
          // which an operator should find out here rather than during an incident.
          <p style={{ margin: "var(--s3) 0 0", color: "var(--deny-text)", maxWidth: "var(--measure)" }}>
            <strong>No evidence store is configured.</strong> Decisions are still signed, and
            they are being emitted to nowhere. Set{" "}
            <span className="mono">AUTHOXI_LOSS_EVENTS_URL</span> before you need the trail —
            afterwards is too late, because we do not keep a copy.
          </p>
        )}
      </div>

      <div className="row wrap" style={{ margin: "var(--s5) 0" }}>
        <a className="btn btn-go" href="/compliance/pack" rel="external" data-testid="compliance-pack">
          Export a compliance pack
        </a>
        <a className="btn" href={`${TOOLS}/verify`}>
          Verify a decision
        </a>
        <span className="sm muted">
          Packs state what they do <strong>not</strong> cover, in the pack.
        </span>
      </div>

      <div className="rule">
        <span className="label">Signed by a human</span>
      </div>
      {settled.loading && <State kind="loading" title="Loading…" />}
      {settled.data && settled.data.pending.length === 0 && (
        <State
          kind="empty"
          title="Nobody has signed anything yet."
          detail="This lists the escalations a person resolved. Decisions the gate made alone are in the record."
        />
      )}
      <div className="timeline">
        {(settled.data?.pending ?? []).slice(0, 20).map((p) => (
          <div className={`tl ${p.resolution === "approve" ? "allow" : "deny"}`} key={p.pending_id}>
            <div className="tl-time">{fmtWhen(p.created_at)}</div>
            <div className="tl-what">
              <span className="mono">{p.agent_id}</span> · {p.action}
              {p.amount !== "0" && ` · ${fmtMoney(p.amount, p.currency)}`}
            </div>
            <div className="tl-why">
              {p.resolution === "approve" ? "approved" : "refused"} by a named human · ed25519 ✓
            </div>
          </div>
        ))}
      </div>

      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">What people did to the control plane</span>
      </div>
      <p className="sm muted" style={{ marginBottom: "var(--s3)" }}>
        {audit.data?.note ??
          "Deliberately a different record from what agents did — different question, different answer."}
      </p>
      {audit.data && audit.data.events.length === 0 && (
        <State kind="empty" title="No control-plane changes recorded." detail="Nobody has changed a role or a policy." />
      )}
      <div className="card pad">
        <div className="stack">
          {(audit.data?.events ?? []).slice(0, 20).map((e, i) => (
            <div className="between" key={i}>
              <span className="mono sm">{String(e.action ?? e.event ?? "change")}</span>
              <span className="sm muted">{String(e.actor ?? "—")}</span>
              <span className="sm muted">{fmtWhen(String(e.created_at ?? ""))}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card pad" style={{ marginTop: "var(--s4)" }}>
        <p className="label">What a stranger can check</p>
        <pre className="code" style={{ marginTop: "var(--s3)" }}>{`npx -p @authoxi/js authoxi-verify decision.json

# ✓ emitter signature valid
# ✓ approver signature valid — ops@acme.com`}</pre>
        <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
          No account and no call to us. If verifying our evidence required our service, the
          evidence would only be as durable as our uptime.
        </p>
      </div>
    </>
  );
}
